/**
 * Composer text mechanics — V7-04 §7.
 *
 * Pure string functions, deliberately: formatting a selection and expanding a
 * shortcode are the two things in §7 with real edge cases (an empty selection,
 * a marker already there, a shortcode inside a word), and testing them through
 * a rendered textarea would be testing React instead of the behaviour.
 *
 * §7 asks for "the existing limited markdown" extended, **not** a full document
 * editor. So the wire format stays plain text with markers a person can type
 * by hand — the toolbar inserts exactly what somebody typing `**bold**` would
 * have written, and a message composed either way is the same message.
 */

/** What the toolbar can apply. */
export type Format = 'bold' | 'italic' | 'code' | 'codeBlock' | 'bullet' | 'numbered' | 'link';

const WRAPPERS: Record<'bold' | 'italic' | 'code', string> = {
  bold: '**',
  italic: '_',
  code: '`',
};

export interface Selection {
  text: string;
  start: number;
  end: number;
}

export interface FormatResult {
  text: string;
  /** Where the caret should land — the whole point of returning it. */
  selectionStart: number;
  selectionEnd: number;
}

/**
 * Apply a format to a selection, or set one up for typing into.
 *
 * Two cases matter and both are here:
 *  - **Something is selected** — it gets wrapped, and stays selected, so a
 *    person can hit bold twice and see it come off.
 *  - **Nothing is selected** — the markers are inserted and the caret is put
 *    *between* them, which is what every editor does and what makes the button
 *    useful before you have typed the word.
 */
export function applyFormat(selection: Selection, format: Format): FormatResult {
  const { text, start, end } = selection;
  const selected = text.slice(start, end);

  if (format === 'bold' || format === 'italic' || format === 'code') {
    const marker = WRAPPERS[format];
    // Already wrapped: take it off rather than nesting it, or a double click
    // on bold produces `****bold****`.
    const before = text.slice(Math.max(0, start - marker.length), start);
    const after = text.slice(end, end + marker.length);
    if (selected && before === marker && after === marker) {
      const next =
        text.slice(0, start - marker.length) + selected + text.slice(end + marker.length);
      return {
        text: next,
        selectionStart: start - marker.length,
        selectionEnd: end - marker.length,
      };
    }

    const next = text.slice(0, start) + marker + selected + marker + text.slice(end);
    return {
      text: next,
      selectionStart: start + marker.length,
      selectionEnd: end + marker.length,
    };
  }

  if (format === 'codeBlock') {
    const fence = '```';
    const body = selected || '';
    const next = `${text.slice(0, start)}${fence}\n${body}\n${fence}${text.slice(end)}`;
    const caret = start + fence.length + 1;
    return { text: next, selectionStart: caret, selectionEnd: caret + body.length };
  }

  if (format === 'link') {
    // `[text](url)`, with the caret in whichever half is still empty.
    const label = selected || 'text';
    const next = `${text.slice(0, start)}[${label}](url)${text.slice(end)}`;
    const urlStart = start + 1 + label.length + 2;
    return { text: next, selectionStart: urlStart, selectionEnd: urlStart + 3 };
  }

  // Lists apply per line across the selection, because a list of one line is
  // rarely what somebody meant to select.
  const lineStart = text.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const lineEndIndex = text.indexOf('\n', end);
  const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
  const block = text.slice(lineStart, lineEnd);
  const lines = block.split('\n');

  const prefixed = lines.map((line, index) =>
    format === 'bullet' ? `- ${line}` : `${index + 1}. ${line}`,
  );
  const next = text.slice(0, lineStart) + prefixed.join('\n') + text.slice(lineEnd);
  const added = prefixed.join('\n').length - block.length;
  return { text: next, selectionStart: lineStart, selectionEnd: lineEnd + added };
}

/**
 * `:thumbsup:` → 👍 — §7.
 *
 * A short list rather than a full emoji database: these are the shortcodes
 * people actually type mid-sentence, and shipping five thousand of them would
 * mean a megabyte of table to save a few keystrokes.
 */
export const SHORTCODES: Record<string, string> = {
  ':thumbsup:': '👍',
  ':+1:': '👍',
  ':thumbsdown:': '👎',
  ':heart:': '❤️',
  ':joy:': '😂',
  ':smile:': '😄',
  ':tada:': '🎉',
  ':eyes:': '👀',
  ':pray:': '🙏',
  ':white_check_mark:': '✅',
  ':check:': '✅',
  ':fire:': '🔥',
  ':rocket:': '🚀',
  ':thinking:': '🤔',
  ':wave:': '👋',
  ':clap:': '👏',
  ':100:': '💯',
  ':warning:': '⚠️',
};

const SHORTCODE_PATTERN = /:[a-z0-9_+-]+:/g;

/**
 * Expand every shortcode in a body.
 *
 * Run on send rather than on every keystroke, so somebody typing `:fire:` sees
 * what they typed until they have finished typing it — replacing text under a
 * caret mid-word is how an autocorrect earns its reputation.
 */
export function expandShortcodes(body: string): string {
  return body.replace(SHORTCODE_PATTERN, (match) => SHORTCODES[match] ?? match);
}
